import client from './client';

// Logins are configured in the backend .env file, so there is no signup or
// password-change endpoint here by design.
export const authApi = {
  login: (payload) => client.post('/auth/login', payload),
  me: () => client.get('/auth/me'),
};

export const uploadApi = {
  /** Sends the chosen files and returns [{ name, url, type, sizeKb }]. */
  files: (fileList, onProgress) => {
    const form = new FormData();
    Array.from(fileList).forEach((f) => form.append('files', f));

    return client.post('/uploads', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
  },
  /** Clears a file that was uploaded but never saved onto a patient. */
  discard: (report) =>
    client.delete('/uploads', {
      params: { publicId: report?.publicId, resourceType: report?.resourceType },
    }),
};

export const patientApi = {
  list: (params) => client.get('/patients', { params }),
  /** Everyone who still owes money, worst first. */
  dues: () => client.get('/patients/dues'),
  get: (id) => client.get(`/patients/${id}`),
  history: (id) => client.get(`/patients/${id}/history`),
  checkMobile: (mobile) => client.get('/patients/check-mobile', { params: { mobile } }),
  create: (payload) => client.post('/patients', payload),
  update: (id, payload) => client.patch(`/patients/${id}`, payload),
  addReport: (id, payload) => client.post(`/patients/${id}/reports`, payload),
  removeReport: (id, reportId) => client.delete(`/patients/${id}/reports/${reportId}`),
};

export const visitApi = {
  queue: (params) => client.get('/visits/queue', { params }),
  todayStats: (params) => client.get('/visits/stats/today', { params }),
  byPatient: (patientId) => client.get(`/visits/patient/${patientId}`),
  range: (params) => client.get('/visits/range', { params }),
  get: (id) => client.get(`/visits/${id}`),
  create: (payload) => client.post('/visits', payload),
  update: (id, payload) => client.patch(`/visits/${id}`, payload),
  setStatus: (id, status) => client.patch(`/visits/${id}/status`, { status }),
  cancel: (id) => client.delete(`/visits/${id}`),
  /** Records one instalment against a visit. The only way money is booked. */
  addPayment: (id, payload) => client.post(`/visits/${id}/payments`, payload),
};

export const consultationApi = {
  byVisit: (visitId) => client.get(`/consultations/visit/${visitId}`),
  byPatient: (patientId) => client.get(`/consultations/patient/${patientId}`),
  last: (patientId, excludeVisit) =>
    client.get(`/consultations/patient/${patientId}/last`, { params: { excludeVisit } }),
  save: (payload) => client.post('/consultations', payload),
  printData: (visitId) => client.get(`/consultations/print/${visitId}`),
  /** Messages the patient on WhatsApp. Sent from the print screen. */
  notify: (visitId) => client.post(`/consultations/notify/${visitId}`),
  applyTemplates: (templateIds) => client.post('/consultations/apply-template', { templateIds }),
};

export const masterApi = {
  medicines: (params) => client.get('/masters/medicines', { params }),
  createMedicine: (payload) => client.post('/masters/medicines', payload),
  updateMedicine: (id, payload) => client.patch(`/masters/medicines/${id}`, payload),
  deleteMedicine: (id) => client.delete(`/masters/medicines/${id}`),
  precautions: (params) => client.get('/masters/precautions', { params }),
  createPrecaution: (payload) => client.post('/masters/precautions', payload),
  updatePrecaution: (id, payload) => client.patch(`/masters/precautions/${id}`, payload),
  deletePrecaution: (id) => client.delete(`/masters/precautions/${id}`),
  /** Clinics and doctors who send patients here. */
  referrals: (params) => client.get('/masters/referrals', { params }),
  createReferral: (payload) => client.post('/masters/referrals', payload),
  updateReferral: (id, payload) => client.patch(`/masters/referrals/${id}`, payload),
  deleteReferral: (id) => client.delete(`/masters/referrals/${id}`),
};

export const inventoryApi = {
  list: (params) => client.get('/inventory', { params }),
  /** A medicine the clinic has just started keeping, with its opening stock. */
  create: (payload) => client.post('/inventory', payload),
  /** Stock coming in, or a correction after a physical count. */
  addStock: (id, payload) => client.post(`/inventory/${id}/stock`, payload),
  movements: (id, params) => client.get(`/inventory/${id}/movements`, { params }),
};

export const settingsApi = {
  get: () => client.get('/settings'),
  /** Clinic name and logo only - readable before signing in. */
  public: () => client.get('/settings/public'),
  update: (payload) => client.patch('/settings', payload),
};

export const reportApi = {
  summary: (params) => client.get('/reports/summary', { params }),
  topDiagnoses: (params) => client.get('/reports/top-diagnoses', { params }),
  topMedicines: (params) => client.get('/reports/top-medicines', { params }),
  followUps: (params) => client.get('/reports/follow-ups', { params }),
};
